import { Property, Type } from '@dipscope/type-manager';
import { MeepleSet, TileBasePlayerDTO } from '../tilebase/TileBasePlayer';
import 'reflect-metadata';
import KTile, { KTileDTO } from './KTile';
import Player from '../../core/Player';
import { Meeple, MeepleDTO, MeepleType, MeepleTypeMap } from '../tilebase/Meeple';
import { PlayerDTO } from '@envclient/envcore';

@Type({ injectable: true, discriminant: 'KPlayer' })
class KPlayer extends Player {
  @Property([String, MeepleSet])
  meeples: Map<MeepleType, MeepleSet> = new Map();
  @Property([Meeple])
  activeMeeples: Set<Meeple> = new Set();
  @Property()
  protected idCount: number = 0;

  @Property([KTile])
  assets: KTile[] = [];

  createMeeples(meepleCount: Map<MeepleType, number>) {
    meepleCount.forEach((count, type) => {
      const meepleSet = new MeepleSet();
      this.meeples.set(type, meepleSet);
      for (let i = 0; i < count; i++) {
        const meeple: Meeple = new Meeple();
        meeple.id = this.idCount;
        meeple.type = type;
        meeple.color = this.color;
        meeple.value = MeepleTypeMap[type];
        meeple.playerId = this.id;
        meepleSet.set.add(meeple);
        this.idCount++;
      }
    });
  }

  activateMeepleByType(type: MeepleType): Meeple {
    if (this.meeples.get(type).set.size === 0) {
      return null;
    }
    const meeple = this.meeples.get(type).set.values().next().value;
    this.meeples.get(type).set.delete(meeple);
    this.activeMeeples.add(meeple);
    return meeple;
  }

  availableTypes(): MeepleType[] {
    return Array.from(this.meeples.entries())
      .filter(([_, set]) => set && set.set.size > 0)
      .map(([type, _]) => type) as MeepleType[];
  }

  getMeepleByType(type: MeepleType): Meeple {
    if (this.meeples.get(type).set.size == 0) return null;
    const meeple = this.meeples.get(type).set.values().next().value;
    return meeple;
  }

  recoverMeeple(meeple: Meeple) {
    meeple.recovered = true;
    this.activeMeeples.delete(meeple);

    // create new meeple with 0 score
    const newMeeple = new Meeple();
    newMeeple.id = meeple.id;
    newMeeple.type = meeple.type;
    newMeeple.value = meeple.value;
    newMeeple.playerId = meeple.playerId;
    newMeeple.color = meeple.color;

    // increase recovered meepleId by 100 (to make it unique - play only once recover animation)
    newMeeple.id += 100;

    this.meeples.get(newMeeple.type).set.add(newMeeple);
  }

  toDTO(): KPlayerDTO {
    const p = super.toDTO();
    const meeples: { [P in MeepleType]?: MeepleDTO[] } = {};
    for (const [key, value] of this.meeples.entries()) {
      meeples[key as MeepleType] = [...value.set.values()].map((m) => m.toDTO());
    }
    const activeMeeples = [...this.activeMeeples.values()].map((m) => m.toDTO());

    return {
      ...p,
      meeples,
      activeMeeples,
      assets: this.assets.map((t) => t.toDTO())
    };
  }
}

export interface KPlayerDTO extends PlayerDTO {
  meeples: { [P in MeepleType]?: MeepleDTO[] };
  activeMeeples: MeepleDTO[];
  assets: KTileDTO[];
}

export default KPlayer;
